import { useAsyncData, useRoute, useRuntimeConfig } from '#imports'
import { computed } from 'vue'

import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { getAsyncDataKey } from '@backmarket/nuxt-module-http-v2/getAsyncDataKey'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { removeEmptyValuesInObject } from '@backmarket/utils/object/removeEmptyValuesInObject'

import { useUrlParams } from '~/scopes/product-core/composables/useUrlParams/useUrlParams'

export function useFetchPickersInternal(route = useRoute()) {
  const { grade, offerType, variantClicked, productId, closestPickerConfig } =
    useUrlParams(route)
  const $httpFetch = useHttpFetch()
  const experiments = useExperiments()
  const config = useRuntimeConfig()

  const pickersQueryParams = computed(() => {
    const values: ApiSpecs['/product-page/products/:productId/pickers']['GET']['query'] =
      {
        grade: grade.value ? String(grade.value) : null,
        special_offer_type: offerType.value ? String(offerType.value) : null,
        premium_grade: true,
        cheapest_by_grade: !variantClicked.value,
        cheapest_for_first_picker: config.public
          .FF_PRODUCT_PAGE_ENABLE_CLOSEST_PICKER_CONFIG
          ? !closestPickerConfig.value
          : false,
        battery_confidence_experiment:
          // eslint-disable-next-line no-nested-ternary
          experiments['experiment.ppBatteryConfidence'] === 'withText'
            ? 2
            : experiments['experiment.ppBatteryConfidence'] === 'withNumbers'
              ? 3
              : 1,
      }

    return removeEmptyValuesInObject(values)
  })

  return useAsyncData(
    () =>
      getAsyncDataKey('pp-pickers', {
        productId,
        ...pickersQueryParams.value,
      }),
    async () => {
      let pickersResponseCacheTagHeader

      const response = await $httpFetch(
        '/product-page/products/:productId/pickers',
        {
          pathParams: { productId },
          query: pickersQueryParams.value,
          onResponse: (context) => {
            if (context.response?.ok) {
              pickersResponseCacheTagHeader =
                context.response?.headers.get('Cache-Tag') ?? ''
            }
          },
        },
      )

      return {
        ...response,
        pickersResponseCacheTagHeader,
      }
    },
    {
      dedupe: 'defer',
      getCachedData: (key, nuxtApp) => {
        // Prevent duplicate calls during SSR: after ProductDetailPageBase's
        // `onServerPrefetch` resolves, child components find the data here
        // instead of triggering a second HTTP call.
        if (import.meta.server || nuxtApp.isHydrating) {
          return nuxtApp.payload.data[key]
        }

        // After hydration: always fetch fresh data.
        // This endpoint is the source of truth for price — never serve stale.
        return undefined
      },
    },
  )
}
